import type { SettingsOutput, StorageTerms } from '@garage/shared';
import { Money, hasAmount } from '../ui/money.js';

export function termsFromSettings(s: SettingsOutput): StorageTerms {
  return {
    freeDays: s.storageFreeDays,
    dayType: s.storageDayType,
    startRule: s.storageStartRule,
    dailyFee: s.storageDailyFee,
    feeTaxMode: s.storageFeeTaxMode,
    termsText: s.storageTermsText,
  };
}

/** Phrase lisible du délai gratuit (« 5 jours calendaires »). */
export function freeDaysLabel(t: Pick<StorageTerms, 'freeDays' | 'dayType'>): string {
  if (t.freeDays === 0) return 'aucun jour gratuit';
  const unit = t.dayType === 'business' ? 'ouvré' : 'calendaire';
  return `${t.freeDays} jour${t.freeDays > 1 ? 's' : ''} ${unit}${t.freeDays > 1 ? 's' : ''}`;
}

/**
 * Résumé des conditions de gardiennage. Le tarif n'apparaît que si l'API l'a renvoyé
 * (absent sans `amounts.view`, ou non saisi par le garage).
 */
export function StorageTermsSummary({ settings, terms, waived = false }: { settings?: SettingsOutput; terms?: StorageTerms; waived?: boolean }) {
  const t = terms ?? (settings ? termsFromSettings(settings) : null);
  if (!t) return null;
  // Gardiennage offert (Q7) : la mention seule, sans aucun tarif ni conditions de frais.
  if (waived) {
    return (
      <p className="rounded-xl bg-emerald-50 p-3 text-sm font-medium text-emerald-900">
        Gardiennage offert par le garage : aucun frais de gardiennage ne vous sera demandé.
      </p>
    );
  }
  return (
    <div className="space-y-1 text-sm text-zinc-700">
      <p>
        Retrait gratuit pendant <strong>{freeDaysLabel(t)}</strong> après la mise à disposition du véhicule.
      </p>
      {hasAmount(t.dailyFee) ? (
        <p>
          Au-delà : <Money value={t.dailyFee} suffix={`${t.feeTaxMode === 'ht' ? 'HT' : 'TTC'} par jour`} />.
        </p>
      ) : null}
      {t.termsText ? <p className="whitespace-pre-line text-zinc-600">{t.termsText}</p> : null}
    </div>
  );
}
